import {
  dynamicMatchHighlight,
  dynamicMatchWithMapping,
  type MatchMode,
} from '@wandb/weave/common/util/columnMatching';
import {groupBy} from 'lodash';
import {type ReactFragment, useCallback, useMemo} from 'react';

import {EMPTY_ARRAY} from '../../../util/constants';
import type * as RunFeed from '../../../util/runfeed';
import * as Run from '../../../util/runs';

type UseTableEditorSearchMatchesOptions = {
  allColumns: string[];
  config: RunFeed.Config;
  fixedColumns: string[];
  searchQuery: string;
  searchMode: MatchMode;
};

type UseTableEditorSearchMatchesHook = {
  fixedColumns: string[];
  hiddenColumns: string[];
  highlightColumn: (column: string) => ReactFragment;
  pinnedColumns: string[];
  visibleColumns: string[];
};

/**
 * Returns search matches based on the search query and mode.
 */
export function useTableEditorSearchMatches({
  allColumns,
  fixedColumns,
  config,
  searchQuery,
  searchMode,
}: UseTableEditorSearchMatchesOptions): UseTableEditorSearchMatchesHook {
  const getSearchMatches = useCallback(
    (columns: string[]) =>
      dynamicMatchWithMapping(searchMode, columns, searchQuery, column =>
        Run.keyStringDisplayName(column)
      ),
    [searchQuery, searchMode]
  );

  const hiddenColumns = useMemo(
    () =>
      getSearchMatches(
        allColumns.filter(cAccessor => !config.columnVisible[cAccessor])
      ),
    [allColumns, config.columnVisible, getSearchMatches]
  );

  const matchingVisibleColumns = useMemo(
    () => getSearchMatches(config.columnOrder),
    [config.columnOrder, getSearchMatches]
  );

  const {pinnedColumns, visibleColumns} = useMemo(
    () =>
      groupBy(matchingVisibleColumns, column => {
        if (fixedColumns.includes(column)) {
          // de-facto filter out fixed columns from pinnedColumns, because
          // all fixed columns are always pinned, but not vice-versa
          return 'fixedColumns';
        }
        if (config.columnPinned[column]) {
          return 'pinnedColumns';
        }
        return 'visibleColumns';
      }),
    [fixedColumns, config.columnPinned, matchingVisibleColumns]
  );

  const highlightColumn = useCallback(
    (column: string) => {
      return dynamicMatchHighlight(
        searchMode,
        Run.keyStringDisplayName(column),
        searchQuery
      );
    },
    [searchMode, searchQuery]
  );

  return {
    fixedColumns,
    hiddenColumns,
    highlightColumn,
    pinnedColumns: pinnedColumns || EMPTY_ARRAY,
    visibleColumns: visibleColumns || EMPTY_ARRAY,
  };
}
